import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';

export interface GameStateInfo {
  dbStatus: string;
  worldName: string;
  starCount: number;
  planetCount: number;
  playerName: string;
  fogReveals: number;
  fogHexes: number;
}

export class GameStateHUD implements GameModule {
  readonly name = 'GameStateHUD';
  readonly state: GameStateInfo = {
    dbStatus: '...',
    worldName: '—',
    starCount: 0,
    planetCount: 0,
    playerName: 'nicht eingeloggt',
    fogReveals: 0,
    fogHexes: 0,
  };

  private _el!: HTMLDivElement;
  private _engine!: Engine;
  private _timer = 0;
  private _startVisible: boolean;

  constructor(startVisible = true) {
    this._startVisible = startVisible;
  }
  private _onDebugToggled = (visible: boolean): void => {
    this._el.style.display = visible ? '' : 'none';
  };
  private _dbEl!: HTMLSpanElement;
  private _weltEl!: HTMLSpanElement;
  private _objEl!: HTMLSpanElement;
  private _playerEl!: HTMLSpanElement;
  private _fogEl!: HTMLSpanElement;

  private _lastDb = '';
  private _lastWorld = '';
  private _lastPlayer = '';

  init(engine: Engine): void {
    this._engine = engine;
    this._el = document.createElement('div');
    Object.assign(this._el.style, {
      position: 'fixed', top: '8px', right: '8px', color: '#8cf',
      fontFamily: 'monospace', fontSize: 'clamp(0.6rem,1.8vw,0.75rem)', lineHeight: '1.6',
      background: 'rgba(0,0,0,0.6)', padding: 'clamp(4px,1vw,6px) clamp(6px,1.5vw,10px)',
      borderRadius: '4px', zIndex: '10000', pointerEvents: 'none',
      textAlign: 'right', whiteSpace: 'pre',
      transition: 'top 0.3s, bottom 0.3s',
      display: this._startVisible ? '' : 'none',
    });

    engine.events.on('planet:hub-opened', () => {
      this._el.style.top = 'auto';
      this._el.style.bottom = '8px';
    });
    engine.events.on('planet:hub-closed', () => {
      this._el.style.bottom = 'auto';
      this._el.style.top = '8px';
    });

    this._dbEl = this._span('#888');
    this._weltEl = this._span('#ff0');
    this._objEl = this._span('#8cf');
    this._playerEl = this._span('#888');
    this._fogEl = this._span('#8cf');

    this._el.append(
      'DB ', this._dbEl, '\n',
      'WELT ', this._weltEl, '\n',
      this._objEl, '\n',
      'SPIELER ', this._playerEl, '\n',
      this._fogEl,
    );
    document.body.appendChild(this._el);
    engine.events.on<boolean>('debug-hud:toggled', this._onDebugToggled);
  }

  update(dt: number): void {
    this._timer += dt;
    if (this._timer < 0.5) return;
    this._timer = 0;

    const s = this.state;

    if (s.dbStatus !== this._lastDb) {
      this._lastDb = s.dbStatus;
      this._dbEl.textContent = s.dbStatus;
      this._dbEl.style.color = s.dbStatus === '...' ? '#888'
        : s.dbStatus.startsWith('ERR') ? '#f00' : '#0f0';
    }
    if (s.worldName !== this._lastWorld) {
      this._lastWorld = s.worldName;
      this._weltEl.textContent = s.worldName;
    }
    if (s.playerName !== this._lastPlayer) {
      this._lastPlayer = s.playerName;
      this._playerEl.textContent = s.playerName;
      this._playerEl.style.color = s.playerName === 'nicht eingeloggt' ? '#888' : '#8cf';
    }

    this._objEl.textContent = 'STERNE ' + s.starCount + ' · PLANETEN ' + s.planetCount;
    this._fogEl.textContent = 'FOG ' + s.fogReveals + ' reveals · ' + s.fogHexes + ' fogged';
  }

  dispose(): void {
    this._engine.events.off('debug-hud:toggled', this._onDebugToggled);
    this._el.remove();
  }

  private _span(color: string): HTMLSpanElement {
    const el = document.createElement('span');
    el.style.color = color;
    return el;
  }
}
